import { storeActions } from "@/constants/store-actions";
import baseMixin from "@/mixins/base-mixin.js";
import { RouteCodeFlags } from "@/constants/schedule-constants";

export async function getAlertReasons(ctu) {
    const alertReasons = await baseMixin.methods.dispatchStoreActionWithLogging(
        storeActions.GET_ALERT_REASONS_BY_CTU,
        {
            ctu: ctu,
        },
        "schedule"
    );

    return Promise.resolve(alertReasons);
}

export function calcDaysBetweenDates(dateString1, dateString2) {
    const date1 = new Date(dateString1);
    const date2 = new Date(dateString2);
    const timeDifference = Math.abs(date2 - date1); // Calculate the time difference in milliseconds
    return Math.ceil(timeDifference / (1000 * 60 * 60 * 24)); // Convert milliseconds to days
}

export function convertDateToDateString(date) {
    // returns YYYY-MM-DD format
    if (date instanceof Date !== true) return;
    return (
        date.getFullYear() +
        "-" +
        ("0" + (date.getMonth() + 1)).slice(-2) +
        "-" +
        ("0" + date.getDate()).slice(-2)
    );
}

export function convertDateStringToDate(dateString) {
    // dateString must be YYYY-MM-DD format
    if (typeof dateString !== "string") return;
    const dateParts = dateString.split("-");
    return new Date(dateParts[0], parseInt(dateParts[1]) - 1, dateParts[2]);
}

export function sumDateString(dateString, daysToAdd) {
    // dateString must be YYYY-MM-DD format
    if (typeof dateString !== "string") return;
    const date = convertDateStringToDate(dateString);
    date.setDate(date.getDate() + daysToAdd);
    return convertDateToDateString(date);
}

export function militaryToTwelveHourTime(timeString) {
    // Expected input: "HH:MM"
    if (typeof timeString !== "string") return;
    let hours = parseInt(timeString.split(":")[0]);
    const minutes = timeString.split(":")[1];
    const meridianNotation = hours > 11 ? "PM" : "AM";

    if (hours > 12) {
        hours -= 12;
    }

    return `${hours}:${minutes} ${meridianNotation}`;
}

export function isDropOffRouteCode(routeCode) {
    if (!routeCode) {
        return false;
    }
    return (
        routeCode.includes(RouteCodeFlags.ALL_DAY_DROP_OFF) ||
        routeCode.includes(RouteCodeFlags.OVERNIGHT_DROP_OFF)
    );
}

export const TimeSlotSelectedLabelTypes = {
    EARLY_BIRD: "EarlyBird",
    DROP_OFF: "DropOff",
    OVERNIGHT: "Overnight",
    INSHOP: "inshop",
    MOBILE: "mobile",
};

export function getTimeSlotSelectedAnalyticsLabel({
    startTime,
    isPremiumAppointment,
    routeCodeId,
    serviceType,
    providerNumber,
}) {
    let timeOrSlotType = startTime;

    if (isPremiumAppointment) {
        timeOrSlotType = TimeSlotSelectedLabelTypes.EARLY_BIRD;
    } else if (routeCodeId?.includes(RouteCodeFlags.OVERNIGHT_DROP_OFF)) {
        timeOrSlotType = TimeSlotSelectedLabelTypes.OVERNIGHT;
    } else if (routeCodeId?.includes(RouteCodeFlags.ALL_DAY_DROP_OFF)) {
        timeOrSlotType = TimeSlotSelectedLabelTypes.DROP_OFF;
    }

    return `${timeOrSlotType}_${serviceType}_${providerNumber}`;
}

export const INSHOP_INITIAL_VISIBLE_TIME_SLOTS = 6;

export function getInshopAppointmentTimeSlots(timeSlots = []) {
    return (timeSlots ?? []).filter((timeSlot) => !isDropOffRouteCode(timeSlot.id));
}

export function isMorningInshopTimeSlot(timeSlot) {
    const hours = parseInt(timeSlot?.startTime?.split(":")[0], 10);
    if (Number.isNaN(hours)) {
        return false;
    }
    return hours < 12;
}

function sortTimeSlotsByStartTime(timeSlots) {
    return [...timeSlots].sort((a, b) => {
        if (a.startTime < b.startTime) return -1;
        if (a.startTime > b.startTime) return 1;
        return 0;
    });
}

export function groupInshopTimeSlotsByTimeOfDay(timeSlots = []) {
    const inshopSlots = getInshopAppointmentTimeSlots(timeSlots);
    const morning = sortTimeSlotsByStartTime(inshopSlots.filter(isMorningInshopTimeSlot));
    const afternoon = sortTimeSlotsByStartTime(
        inshopSlots.filter((timeSlot) => !isMorningInshopTimeSlot(timeSlot))
    );

    return { morning, afternoon };
}

export function formatInshopTimeSlotLabel(timeSlot) {
    return militaryToTwelveHourTime(timeSlot.startTime);
}

export function mapInshopTimeSlotToDisplaySlot(timeSlot) {
    return {
        value: timeSlot.id,
        label: formatInshopTimeSlotLabel(timeSlot),
        routeCodeId: timeSlot.id,
        startTime: timeSlot.startTime,
    };
}

export function getTodayDate() {
    return new Date();
}

export function getTodayDateString() {
    return convertDateToDateString(getTodayDate());
}

export function getMonthEnd(dateStr) {
    // convert string to date, do date calc, then return a string back
    const date = new Date(dateStr.split("-")[0], parseInt(dateStr.split("-")[1]), 0);
    return convertDateToDateString(date);
}

export function getWeekStartDate(dateString) {
    const date = convertDateStringToDate(dateString);
    const dayOfWeek = date.getDay();
    // Subtract the day of the week from date to get the date of Sunday
    const sunday = new Date(date);
    sunday.setDate(sunday.getDate() - dayOfWeek);
    return convertDateToDateString(sunday);
}

export function getWeekEndDate(dateString) {
    const date = convertDateStringToDate(dateString);
    const dayOfWeek = date.getDay();
    const daysUntilSaturday = 6 - dayOfWeek; // Calculate the number of days until Saturday
    // Clone the given date and add the remaining days until Saturday
    const saturday = new Date(date);
    saturday.setDate(date.getDate() + daysUntilSaturday);
    return convertDateToDateString(saturday);
}

export function getNextWeekSunday(dateString) {
    const date = convertDateStringToDate(dateString);
    const dayOfWeek = date.getDay();
    const daysUntilNextSunday = 7 - dayOfWeek; // Calculate the number of days until the next Sunday
    // Clone the given date and add the remaining days until Sunday
    const nextSunday = new Date(date);
    nextSunday.setDate(date.getDate() + daysUntilNextSunday);
    return convertDateToDateString(nextSunday);
}

export function getInitialViewWeeks(todayString, initialViewRowsToShow, preSelectedDate) {
    const weeks = [];
    let weekStartDate = getWeekStartDate(todayString);
    let weekEndDate = getWeekEndDate(todayString);

    if (preSelectedDate) {
        let preSelectedDateMonthEnd = getMonthEnd(preSelectedDate.replace("-mobile", ""));
        let monthEndsByThisWeek = false;
        let i = 0;
        while (!monthEndsByThisWeek && i < 52) {
            if (i > 0) {
                weekStartDate = getNextWeekSunday(weekEndDate);
                weekEndDate = getWeekEndDate(weekStartDate);

                if (
                    (preSelectedDateMonthEnd > weekStartDate &&
                        preSelectedDateMonthEnd < weekEndDate) ||
                    preSelectedDateMonthEnd === weekStartDate ||
                    preSelectedDateMonthEnd === weekEndDate
                ) {
                    weekEndDate = preSelectedDateMonthEnd;
                    monthEndsByThisWeek = true;
                } else if (preSelectedDateMonthEnd < weekEndDate) {
                    // Additional case: if month ends before this week (but not necessarily during it),
                    // still cut the loop here, but don't truncate the week.

                    monthEndsByThisWeek = true;
                }
            }
            weeks.push({
                weekNum: i + 1,
                weekStartDate: weekStartDate,
                weekEndDate: weekEndDate,
            });
            i++;
        }
    } else {
        for (let i = 0; i < initialViewRowsToShow; i++) {
            if (i > 0) {
                weekStartDate = getNextWeekSunday(weekEndDate);
                weekEndDate = getWeekEndDate(weekStartDate);
            }
            weeks.push({
                weekNum: i + 1,
                weekStartDate: weekStartDate,
                weekEndDate: weekEndDate,
            });
        }
        // If any of these weeks is split between two months, then make them 2 separate "weeks"
        // (a week split between two months is considered 2 weeks per business requirements)
        const hasSplitWeek = (week) => {
            return week.weekStartDate.split("-")[1] !== week.weekEndDate.split("-")[1]
                ? true
                : false;
        };
        const splitWeekIndex = weeks.findIndex(hasSplitWeek);

        if (splitWeekIndex > -1) {
            const week1 = [];
            const week2 = [];
            let switchToWeek2 = false;

            for (let j = 0; j < 7; j++) {
                const newDate = convertDateStringToDate(weeks[splitWeekIndex].weekStartDate);
                newDate.setDate(newDate.getDate() + j);
                if (newDate.getDate() === 1) switchToWeek2 = true;
                if (switchToWeek2) {
                    week2.push(convertDateToDateString(newDate));
                } else {
                    week1.push(convertDateToDateString(newDate));
                }
            }

            const week1EndDate = week1[week1.length - 1];
            const week2StartDate = week2[0];

            if (week1EndDate < todayString) {
                // replace week 1 with week 2
                weeks[splitWeekIndex].weekStartDate = week2StartDate;
            } else {
                const newWeek = {
                    weekNum: weeks[splitWeekIndex].weekNum,
                    weekStartDate: week2StartDate,
                    weekEndDate: weeks[splitWeekIndex].weekEndDate,
                };
                weeks[splitWeekIndex].weekEndDate = week1EndDate;
                weeks.splice(splitWeekIndex + 1, 0, newWeek);
                weeks.pop();
                weeks.forEach((item, index) => {
                    if (index > splitWeekIndex) {
                        item.weekNum = item.weekNum + 1;
                    }
                });
            }
        }
    }
    return weeks;
}

export const NEXT_AVAILABLE_WEEKDAYS = ["Sun", "Mon", "Tues", "Wed", "Thurs", "Fri", "Sat"];
export const SCHEDULING_MONTH_ABBRS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];

/**
 * Returns sorted YYYY-MM-DD strings for days that have at least one time slot.
 * @param {Array<{ date: string, timeSlots?: Array<unknown> }> | null | undefined} days
 */
export function getAvailableDatesFromProviderDays(days) {
    return (days ?? [])
        .filter((day) => day.timeSlots?.length > 0)
        .map((day) => day.date)
        .sort();
}
